import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useNotificationStore, useAuthStore } from '../store';
import { notificationsAPI } from '../lib/api';
import { NOTIF_TYPE_CONFIG, formatTimeAgo } from './NotificationsDropdown';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Bell, Check, Trash2, Settings2, Loader2 } from 'lucide-react';

const DISCORD_TYPE_LABELS = {
  task_assigned: 'Asignación de tareas',
  meeting_invite: 'Invitación a reunión',
  produce_content: 'Te toca producir (contenido)',
  review_content: 'Revisión interna (Karmele)',
  schedule_content: 'A programar (Ana)',
  request_changes: 'Cambios solicitados',
  overdue_publication: 'Publicación atrasada',
  mention: 'Menciones (@)',
  task_due: 'Vence hoy / mañana',
  meeting_today: 'Reunión hoy (recordatorio)',
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { notifications, fetchNotifications, markAsRead, markAllAsRead, clearNotification } = useNotificationStore();
  const [loading, setLoading] = useState(true);
  const [discordCfg, setDiscordCfg] = useState(null);
  const [canEditCfg, setCanEditCfg] = useState(false);
  const [receivesDiscord, setReceivesDiscord] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchNotifications();
      try {
        const cfg = await notificationsAPI.getDiscordConfig();
        setDiscordCfg(cfg.types || {});
        setCanEditCfg(!!cfg.can_edit);
        setReceivesDiscord(cfg.receives_discord !== false);
      } catch (e) {}
      setLoading(false);
    })();
    const id = setInterval(() => fetchNotifications(), 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleClick = (n) => {
    if (!n.read) markAsRead(n.id);
    if (n.link?.startsWith('http')) window.open(n.link, '_blank', 'noopener'); else if (n.link) navigate(n.link);
  };

  const toggleDiscord = async (type, value) => {
    const next = { ...discordCfg, [type]: value };
    setDiscordCfg(next);
    setSavingCfg(true);
    try {
      await notificationsAPI.updateDiscordConfig(next);
      toast.success('Configuración de Discord actualizada');
    } catch (e) {
      toast.error('No se pudo guardar la configuración');
      setDiscordCfg(discordCfg);
    }
    setSavingCfg(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="notifications-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Bell className="w-7 h-7 text-primary" />
            Notificaciones
          </h1>
          <p className="text-muted-foreground mt-1">
            Solo lo importante y lo que te afecta a ti. {unreadCount > 0 ? `${unreadCount} sin leer.` : 'Estás al día.'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" onClick={markAllAsRead} data-testid="page-mark-all-read-btn">
            <Check className="w-4 h-4 mr-2" />
            Marcar todas como leídas
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de notificaciones */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Tus notificaciones</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Bell className="w-12 h-12 text-muted-foreground opacity-30 mb-3" />
                  <p className="text-muted-foreground">No tienes notificaciones</p>
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((n) => {
                    const cfg = NOTIF_TYPE_CONFIG[n.type] || NOTIF_TYPE_CONFIG.comment;
                    const Icon = cfg.icon;
                    return (
                      <div
                        key={n.id}
                        className={`group flex gap-3 p-4 hover:bg-muted/40 cursor-pointer transition-colors ${!n.read ? 'bg-primary/5' : ''}`}
                        onClick={() => handleClick(n)}
                        data-testid={`page-notification-${n.id}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${cfg.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm font-medium ${!n.read ? 'text-foreground' : 'text-muted-foreground'}`}>{n.title}</p>
                            {!n.read && <span className="w-2 h-2 bg-primary rounded-full shrink-0" />}
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border-0 ${cfg.color}`}>{cfg.label}</Badge>
                            <span className="text-xs text-muted-foreground">{formatTimeAgo(n.created_at)}</span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive shrink-0"
                          onClick={(e) => { e.stopPropagation(); clearNotification(n.id); }}
                          data-testid={`page-notification-delete-${n.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Configuración de Discord (solo CEO) */}
        <div>
          <Card data-testid="discord-config-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-primary" />
                Avisos por Discord
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <p className="text-sm text-muted-foreground mb-3">
                {receivesDiscord
                  ? 'Elige qué tipos de notificación quieres recibir también en TU canal interno de Discord. Todo lo verás siempre aquí en el portal.'
                  : 'Como CEO, recibes las notificaciones solo aquí en el portal (los canales de Discord de las CEO son para vuestras conversaciones, no para avisos).'}
              </p>
              {receivesDiscord && discordCfg && Object.keys(DISCORD_TYPE_LABELS).map((type) => (
                <div key={type} className="flex items-center justify-between py-2 border-b last:border-0">
                  <span className="text-sm">{DISCORD_TYPE_LABELS[type]}</span>
                  <Switch
                    checked={!!discordCfg[type]}
                    disabled={!canEditCfg || savingCfg}
                    onCheckedChange={(v) => toggleDiscord(type, v)}
                    data-testid={`discord-toggle-${type}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </motion.div>
  );
}
